<template>
  <div class="tw-flex items-start space-x-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors cursor-pointer">
    <!-- File Icon -->
    <div class="tw-flex-shrink-0">
      <div :class="fileIconClass" class="w-10 h-10 rounded-lg tw-flex items-center justify-center">
        <Icon :name="fileIcon" class="w-5 h-5 text-white"/>
      </div>
    </div>

    <!-- File Info -->
    <div class="tw-flex-1 min-w-0">
      <div class="tw-flex items-center justify-between">
        <div class="tw-flex items-center gap-2 flex-1 min-w-0">
          <h4 class="text-sm font-medium text-gray-900 truncate">{{ file.name }}</h4>
          <span
            v-if="file.is_featured"
            class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0"
          >
            Featured
          </span>
        </div>
        <span v-if="file.size" class="text-xs text-gray-500 tw-flex-shrink-0 ml-2">{{ file.size }}</span>
      </div>

      <p v-if="file.description" class="text-xs text-gray-600 mt-1 line-clamp-2">
        {{ file.description }}
      </p>

      <div class="tw-flex items-center mt-2 space-x-2">
        <span :class="fileTypeClass" class="px-2 py-1 rounded-full text-xs font-medium">
          {{ fileTypeLabel }}
        </span>

        <!-- External link indicator for YouTube, Spotify, etc. -->
        <Icon v-if="isExternalLink" name="external-link" class="w-3 h-3 text-gray-400"/>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import Icon from '@/components/Icon/Icon.vue'

  export default defineComponent({
    name: 'FilePreview',
    components: {
      Icon
    },
    props: {
      file: {
        type: Object,
        required: true
      }
    },
    computed: {
      fileType() {
        // Try multiple possible properties for file type
        const type = this.file.type || this.file.mimetype || this.file.mimeType || 'unknown'
        return type.toLowerCase()
      },
      fileIcon() {
        const type = this.fileType
        const iconMap = {
          // PDFs
          'application/pdf': 'file-pdf',
          pdf: 'file-pdf',

          // Images
          'image/jpeg': 'file-image',
          'image/jpg': 'file-image',
          'image/png': 'file-image',
          'image/gif': 'file-image',
          'image/webp': 'file-image',
          jpg: 'file-image',
          jpeg: 'file-image',
          png: 'file-image',
          gif: 'file-image',
          webp: 'file-image',

          // Excel
          'application/vnd.ms-excel': 'file-spreadsheet',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'file-spreadsheet',
          excel: 'file-spreadsheet',
          xls: 'file-spreadsheet',
          xlsx: 'file-spreadsheet',

          // Audio
          'audio/mpeg': 'musical-note',
          'audio/mp3': 'musical-note',
          'audio/wav': 'musical-note',
          'audio/aac': 'musical-note',
          'audio/flac': 'musical-note',
          'audio/ogg': 'musical-note',
          'audio/m4a': 'musical-note',
          mp3: 'musical-note',
          wav: 'musical-note',
          mpeg: 'musical-note',
          aac: 'musical-note',
          flac: 'musical-note',
          ogg: 'musical-note',
          m4a: 'musical-note',

          // Video
          'video/mp4': 'file-video',
          'video/avi': 'file-video',
          'video/mov': 'file-video',
          'video/wmv': 'file-video',
          'video/flv': 'file-video',
          'video/webm': 'file-video',
          'video/youtube': 'file-video',
          mp4: 'file-video',
          avi: 'file-video',
          mov: 'file-video',
          wmv: 'file-video',
          flv: 'file-video',
          webm: 'file-video',
          youtube: 'file-video',

          // Special types
          'audio/spotify': 'musical-note',
          spotify: 'musical-note',
          link: 'file-link',
          video_link: 'file-video',
          article: 'file-text',
          article_link: 'file-link',

          // Documents
          'application/vnd.ms-powerpoint': 'file-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'file-powerpoint',
          'application/msword': 'file-word',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'file-word',
          'text/plain': 'file-text',
          powerpoint: 'file-powerpoint',
          ppt: 'file-powerpoint',
          pptx: 'file-powerpoint',
          word: 'file-word',
          doc: 'file-word',
          docx: 'file-word',
          text: 'file-text',
          txt: 'file-text',
          md: 'file-text',
          markdown: 'file-text',

          // Archives
          'application/zip': 'file-zip',
          'application/x-rar-compressed': 'file-zip',
          zip: 'file-zip',
          rar: 'file-zip',
          '7z': 'file-zip',

          default: 'document'
        }

        // Check for exact match first, then check if it starts with a category
        if (iconMap[type]) {
          return iconMap[type]
        }

        // Check for category matches (e.g., audio/, video/, image/)
        if (type.startsWith('audio/')) return 'musical-note'
        if (type.startsWith('video/')) return 'file-video'
        if (type.startsWith('image/')) return 'file-image'
        if (type.startsWith('text/')) return 'file-text'

        return iconMap.default
      },
      fileIconClass() {
        const type = this.fileType
        const colorMap = {
          'application/pdf': 'bg-red-500',
          'audio/mpeg': 'bg-blue-500',
          'audio/mp3': 'bg-blue-500',
          'audio/wav': 'bg-blue-500',
          'audio/aac': 'bg-blue-500',
          'video/mp4': 'bg-orange-500',
          'video/youtube': 'bg-red-600',
          'audio/spotify': 'bg-green-600',
          pdf: 'bg-red-500',
          mp3: 'bg-blue-500',
          wav: 'bg-blue-500',
          mpeg: 'bg-blue-500',
          mp4: 'bg-orange-500',
          youtube: 'bg-red-600',
          spotify: 'bg-green-600',
          link: 'bg-blue-500',
          video_link: 'bg-orange-500',
          default: 'bg-gray-400'
        }
        
        if (colorMap[type]) {
          return colorMap[type]
        }
        
        if (type.startsWith('audio/')) return 'bg-blue-500'
        if (type.startsWith('video/')) return 'bg-orange-500'
        if (type.startsWith('image/')) return 'bg-purple-500'
        if (type.startsWith('application/pdf')) return 'bg-red-500'
        
        return colorMap.default
      },
      fileTypeLabel() {
        const type = this.fileType
        const labelMap = {
          'application/pdf': 'PDF',
          'audio/mpeg': 'Audio',
          'audio/mp3': 'Audio',
          'audio/wav': 'Audio',
          'audio/aac': 'Audio',
          'audio/flac': 'Audio',
          'audio/ogg': 'Audio',
          'audio/m4a': 'Audio',
          'video/mp4': 'Video',
          'video/avi': 'Video',
          'video/mov': 'Video',
          'video/youtube': 'YouTube',
          'audio/spotify': 'Spotify',
          pdf: 'PDF',
          mp3: 'Audio',
          wav: 'Audio',
          mpeg: 'Audio',
          mp4: 'Video',
          youtube: 'YouTube',
          spotify: 'Spotify',
          link: 'Link',
          video_link: 'Video',
          article: 'Article',
          article_link: 'Article',
          default: 'File'
        }
        
        if (labelMap[type]) {
          return labelMap[type]
        }
        
        if (type.startsWith('audio/')) return 'Audio'
        if (type.startsWith('video/')) return 'Video'
        if (type.startsWith('image/')) return 'Image'
        if (type.startsWith('application/pdf')) return 'PDF'
        if (type.startsWith('text/')) return 'Text'
        
        return labelMap.default
      },
      fileTypeClass() {
        const type = this.fileType
        const classMap = {
          'application/pdf': 'bg-red-100 text-red-800',
          'audio/mpeg': 'bg-blue-100 text-blue-800',
          'audio/mp3': 'bg-blue-100 text-blue-800',
          'audio/wav': 'bg-blue-100 text-blue-800',
          'video/mp4': 'bg-orange-100 text-orange-800',
          'video/youtube': 'bg-red-100 text-red-800',
          'audio/spotify': 'bg-green-100 text-green-800',
          pdf: 'bg-red-100 text-red-800',
          mp3: 'bg-blue-100 text-blue-800',
          wav: 'bg-blue-100 text-blue-800',
          mpeg: 'bg-blue-100 text-blue-800',
          mp4: 'bg-orange-100 text-orange-800',
          youtube: 'bg-red-100 text-red-800',
          spotify: 'bg-green-100 text-green-800',
          link: 'bg-blue-100 text-blue-800',
          video_link: 'bg-orange-100 text-orange-800',
          article: 'bg-gray-100 text-gray-800',
          default: 'bg-gray-100 text-gray-800'
        }
        
        if (classMap[type]) {
          return classMap[type]
        }
        
        if (type.startsWith('audio/')) return 'bg-blue-100 text-blue-800'
        if (type.startsWith('video/')) return 'bg-orange-100 text-orange-800'
        if (type.startsWith('image/')) return 'bg-purple-100 text-purple-800'
        if (type.startsWith('application/pdf')) return 'bg-red-100 text-red-800'
        if (type.startsWith('text/')) return 'bg-gray-100 text-gray-800'
        
        return classMap.default
      },
      isExternalLink() {
        return ['youtube', 'spotify'].includes(this.fileType) ||
          (this.file.url && (this.file.url.includes('youtube.com') || this.file.url.includes('spotify.com')))
      }
    }
  })
</script>

<style scoped>
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
</style>
